import { useEffect, useState } from 'react';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import { motion } from 'framer-motion';
import { FiSearch } from 'react-icons/fi';
import { db } from '../../firebase';

interface Brochure {
  id: string;
  title: string;
  description: string;
  category: string;
  fileUrl: string;
  thumbnailUrl: string;
  pageCount: string;
  fileSize: string;
  createdAt: any; // Firestore timestamp
}

export default function BrochurePage() {
  const [brochures, setBrochures] = useState<Brochure[]>([]);
  const [filteredBrochures, setFilteredBrochures] = useState<Brochure[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchBrochures = async () => {
      try {
        const q = query(collection(db, 'downloads'), orderBy('createdAt', 'desc'));
        const querySnapshot = await getDocs(q);
        
        const brochuresData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          // Ensure all required fields have default values
          thumbnailUrl: doc.data().thumbnailUrl || '',
          pageCount: doc.data().pageCount || 'N/A',
          fileSize: doc.data().fileSize || 'N/A',
        })) as Brochure[];
        
        setBrochures(brochuresData);
        setFilteredBrochures(brochuresData);
        setIsLoading(false);
      } catch (err) {
        console.error('Error fetching brochures:', err);
        setError('Failed to load brochures. Please try again later.');
        setIsLoading(false);
      }
    };

    fetchBrochures();
  }, []);

  // Handle brochure download
  const handleDownload = (fileUrl: string, title: string) => {
    try {
      if (!fileUrl) {
        throw new Error('No file URL provided');
      }
      
      // Create a temporary anchor element
      const a = document.createElement('a');
      
      // Modify the URL to force download
      let downloadUrl = fileUrl;
      // If it's a Firebase Storage URL, add the download parameter
      if (fileUrl.includes('firebasestorage.googleapis.com')) {
        const url = new URL(fileUrl);
        // Remove any existing download parameters
        url.searchParams.delete('alt');
        url.searchParams.delete('token');
        // Add download parameter
        url.searchParams.append('alt', 'media');
        downloadUrl = url.toString();
      }
      
      // Set the download attribute with the correct file extension
      const fileName = title.endsWith('.pdf') ? title : `${title}.pdf`;
      
      a.href = downloadUrl;
      a.download = fileName;
      a.style.display = 'none';
      
      // Append to body, click and remove
      document.body.appendChild(a);
      a.click();
      
      // Clean up
      setTimeout(() => {
        document.body.removeChild(a);
      }, 100);
      
    } catch (error: unknown) {
      console.error('Error downloading brochure:', error);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
      // Fallback to opening in new tab if download fails
      window.open(fileUrl, '_blank', 'noopener,noreferrer');
    }
  };

  // Format file size to human readable format
  const formatFileSize = (size: string) => {
    if (!size) return 'N/A';
    const bytes = parseInt(size, 10);
    if (isNaN(bytes)) return 'N/A';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Filter brochures based on search term and selected category
  useEffect(() => {
    let result = [...brochures];
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        brochure =>
          brochure.title.toLowerCase().includes(term) ||
          brochure.description.toLowerCase().includes(term) ||
          brochure.category.toLowerCase().includes(term)
      );
    }
    
    if (selectedCategory !== 'All') {
      result = result.filter(brochure => brochure.category === selectedCategory);
    }
    
    setFilteredBrochures(result);
  }, [searchTerm, selectedCategory, brochures]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-900 text-white p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 text-white p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-900/50 border border-red-700 text-red-100 px-4 py-3 rounded">
            <p>Error loading brochures: {error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-300 mb-4">
            Our Brochures
          </h1>
          <p className="text-lg text-gray-300 max-w-3xl mx-auto">
            Explore our collection of brochures and download the ones you're interested in.
          </p>
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FiSearch className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search brochures..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-600 rounded-md bg-gray-800 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div className="w-full sm:w-64">
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="block w-full pl-3 pr-10 py-2 border border-gray-600 rounded-md bg-gray-800 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {['All', ...Array.from(new Set(brochures.map(b => b.category))).filter(Boolean)].map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Brochures Grid */}
        {filteredBrochures.length === 0 ? (
          <div className="text-center py-12 border-2 border-dashed border-gray-700 rounded-lg">
            <p className="text-gray-400 text-lg">No brochures found matching your criteria.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredBrochures.map((brochure) => (
              <motion.div
                key={brochure.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="bg-gray-800 rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300 border border-gray-700"
              >
                <div className="h-48 bg-gray-700 flex items-center justify-center relative overflow-hidden">
                  {brochure.thumbnailUrl ? (
                    <img
                      src={brochure.thumbnailUrl}
                      alt={brochure.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="text-gray-400 text-center p-4">
                      <svg
                        className="w-16 h-16 mx-auto mb-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      <span className="text-sm">No preview available</span>
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end p-4">
                    <span className="inline-block bg-blue-600 text-white text-xs px-2 py-1 rounded-full">
                      {brochure.category}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-white mb-2 line-clamp-2">
                    {brochure.title}
                  </h3>
                  <p className="text-gray-400 text-sm mb-4 line-clamp-2">
                    {brochure.description}
                  </p>
                  <div className="flex justify-between items-center text-sm text-gray-400 mb-4">
                    <span>{brochure.pageCount} pages</span>
                    <span>{formatFileSize(brochure.fileSize)}</span>
                  </div>
                  <div className="flex space-x-3">
                    <button
                      onClick={() => window.open(brochure.fileUrl, '_blank', 'noopener,noreferrer')}
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-l-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                    >
                      Preview
                    </button>
                    <button
                      onClick={() => handleDownload(brochure.fileUrl, brochure.title)}
                      className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-r-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors"
                    >
                      Download
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}